<template>
  <div>
    <AiActionWrapper v-if="currentStep === steps.DESCRIPTION">
      <template v-slot:content>
        <div>
          <p class="h6 blue--text text--lighten-1 mb-1">
            {{ $t('ai_features.tasks_suggestion', { tasks: 1 }) }}
          </p>

          <p class="body-2 mb-0">
            {{ $t('ai_features.tasks_suggestion_message') }}
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <Button
          size="small"
          :text="$t('ai_features.suggest_tasks_for_this_rev')"
          @click="handleFetchTasks"
        />
      </template>
    </AiActionWrapper>
    <AiActionWrapper v-if="currentStep === steps.LOADING">
      <template v-slot:content>
        <div v-if="error" class="error-container px-3 py-2">
          <div class="d-flex align-center mb-1">
            <v-icon class="mr-1" size="16" color="red lighten-2"
              >mdi-alert-outline</v-icon
            >
            <p class="subtitle-2 font-bold red--text text--lighten-2 ma-0 pa-0">
              {{ $t('ai_features.suggestion_error_title') }}
            </p>
          </div>

          <p class="caption red--text text--lighten-2 mb-1">
            {{ $t('ai_features.suggestion_error_message') }}
          </p>
        </div>
        <div
          v-else
          class="d-flex flex-column w-100 justify-center align-center py-8 px-"
        >
          <p class="body-2 blue--text text--lighten-1 mb-1">
            {{ $t(`ai_features.${loadingText}`) }}
          </p>

          <div class="w-100 px-12 py-0">
            <v-progress-linear
              rounded
              class="mt-3"
              color="blue lighten-1"
              height="10"
              model-value="60"
              indeterminate
            ></v-progress-linear>
          </div>
        </div>
      </template>
    </AiActionWrapper>
    <AiActionWrapper v-if="currentStep === steps.SELECTION && tasks.length">
      <template v-slot:content>
        <div>
          <p class="h6 blue--text text--lighten-1 mb-1">
            {{
              $t('ai_features.tasks_suggestion_total', { tasks: tasks.length })
            }}
          </p>
          <p class="body-2 blue-grey--text text--lighten-2 mb-1">
            {{ $t('ai_features.tasks_suggestion_result') }}
          </p>

          <p class="label blue--text text--lighten-1 my-5">
            {{
              $t('ai_features.selected_tasks', { tasks: numberSelectedTasks })
            }}
          </p>

          <div v-for="(task, index) in tasks" :key="index" class="mt-4">
            <AiTask
              :task="task"
              :taskIndex="index"
              :selectedTasks="selectedTasks"
              @selectTask="e => handleSelectTask(e, index)"
            />
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <Button
          v-if="hasSelectedTasks"
          size="small"
          :text="$t('ai_features.create_selected_tasks')"
          @click="handleCreate"
        />
      </template>
    </AiActionWrapper>
  </div>
</template>

<script>
import Button from '../UI/Button.vue'
import AiActionWrapper from './AiActionWrapper.vue'
import AiTask from './AiTask.vue'
import getEntityId from '../../lib/getEntityId'
import api from '@/api'
import { mapGetters } from 'vuex'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

const steps = {
  DESCRIPTION: 'description',
  SELECTION: 'selection',
  LOADING: 'loading',
}

const revModelTypes = {
  improvementIdea: 'improvement-ideas',
  problemSolving: 'problem-solving',
  quickAction: 'quick-actions',
}

export default {
  name: 'AiTasksSuggestion',
  components: {
    Button,
    AiActionWrapper,
    AiTask,
  },

  props: {
    rev: Object,
    skipDescription: Boolean,
  },

  data() {
    return {
      steps,
      currentStep: steps.DESCRIPTION,
      tasks: [],
      selectedTasks: [],
      loadingText: 'generating_tasks',
      error: false,
    }
  },

  mounted() {
    if (this.skipDescription) {
      this.handleFetchTasks()
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'siteId']),

    hasSelectedTasks() {
      return this.selectedTasks.length > 0
    },

    numberSelectedTasks() {
      return this.selectedTasks.length
    },

    revType() {
      return this.rev?.entityType ?? this.rev?.type
    },

    revId() {
      return getEntityId(this.rev)
    },
  },

  methods: {
    async handleFetchTasks() {
      this.currentStep = steps.LOADING

      const { data } = await api.ai.getTasks(
        this.organizationId,
        revModelTypes[this.revType],
        this.revId
      )

      this.tasks = data.suggestions.map(suggestion => {
        return {
          title: suggestion,
        }
      })

      this.currentStep = steps.SELECTION
    },

    handleSelectTask(value, taskIndex) {
      if (!value) {
        return (this.selectedTasks = this.selectedTasks.filter(
          taskInd => taskInd !== taskIndex
        ))
      }

      this.selectedTasks = [...this.selectedTasks, taskIndex]
    },

    cleanData() {
      this.tasks = []
      this.selectedTasks = []
      this.loadingText = 'generating_tasks'
    },

    async handleCreate() {
      this.loadingText = 'creating_tasks'
      this.currentStep = steps.LOADING

      const filteredArray = this.tasks.filter((task, taskIndex) =>
        this.selectedTasks.some(ind => taskIndex === ind)
      )

      const arrayOfPromises = filteredArray.map(task =>
        api.tasks.create(this.organizationId, {
          name: task.title,
          description: task.description,
          entityType: this.revType,
          entityId: this.revId,
          site: this.siteId,
          isPrivate: false,
          status: 'open',
        })
      )

      await Promise.all(arrayOfPromises)

      this.$emit('handleDialogClose')

      this.cleanData()
      eventHub.$emit(HUB_EVENTS.AI.TASK_CREATED)
    },
  },
}
</script>

<style scoped lang="scss">
.error-container {
  border: 1px solid $color-red;
  background: rgba(248, 43, 96, 0.2);
  border-radius: 3px;
}
</style>
